import type { AuthUser } from '@/lib/auth';
import { IS_RUMIO, IS_SAAS } from '@/config/site';
import { isDesigner } from '@/lib/roles';
import { DESIGNER_HOME } from '@/config/designer-surface';

// Mirror of useProducts' public-settings cache key. `useSettings()` warms this
// on every public page load (incl. the login/signup pages), so it's populated
// by the time a user authenticates.
const SETTINGS_CACHE_KEY = 'rumio-settings-cache';

/**
 * Per-site `redirect_authed_to_app` flag, read from the warmed public-settings
 * cache. When true, the tenant routes every authenticated user into the
 * embedded app. Defaults to false (no behavior change) when the cache is cold
 * or unreadable — admins then keep landing on `/admin`.
 */
function siteRedirectsAuthedToApp(): boolean {
  try {
    if (typeof localStorage === 'undefined') return false;
    const raw = localStorage.getItem(SETTINGS_CACHE_KEY);
    if (!raw) return false;
    const data = JSON.parse(raw) as { settings?: Record<string, unknown> };
    return data?.settings?.redirect_authed_to_app === true;
  } catch {
    return false;
  }
}

/**
 * Default landing path after a successful authentication, given the
 * freshly-fetched user. Callers must still honor an explicit `?redirect=` /
 * `location.state.from` deep-link *before* falling back to this.
 *
 * - Rumio platform (rumio.world): always the workspace admin.
 * - Tenant/platform admins: the admin panel — UNLESS the tenant has
 *   `redirect_authed_to_app` set, in which case admins go to `/` too so
 *   AuthorizedHomeRedirect carries them into the embedded app (which is the
 *   product). Admins keep manual access to `/admin` and to marketing via
 *   `?marketing=1`.
 * - SaaS tenants (e.g. whatarewemakingfordinner, vakta-systems): subscribers go
 *   to `/`, where AuthorizedHomeRedirect silently bounces a non-admin subscriber
 *   into the tenant's primary experience (the first installed app mount).
 *   Unsubscribed customers go somewhere they can actually pay:
 *     - they picked a tier on /pricing → straight to checkout for that plan;
 *     - otherwise → `/pricing` to choose one.
 *   (`/pricing` on a tenant is CMS marketing content, so landing there without
 *   a plan in hand used to be a dead end.)
 * - Everyone else (ecommerce, lodging, museum, …): the `/account` area.
 */
export function resolvePostAuthDest(user: AuthUser | null, planSlug?: string | null): string {
  if (IS_RUMIO) return isDesigner(user?.role) ? DESIGNER_HOME : '/admin';

  // Designers land in the admin shell too, but at a page they can actually
  // open — AdminLayout would bounce them off /admin (the revenue dashboard).
  if (isDesigner(user?.role)) return DESIGNER_HOME;

  const isAdmin = user?.role === 'admin' || !!user?.isPlatformAdmin;
  if (isAdmin) return siteRedirectsAuthedToApp() ? '/' : '/admin';

  if (IS_SAAS) {
    if (user?.hasActiveSubscription) return '/';
    return planSlug
      ? `/account/billing?plan=${encodeURIComponent(planSlug)}`
      : '/pricing';
  }

  return '/account';
}
